import { useQueryClient } from '@tanstack/react-query';
import type { FC, ReactNode } from 'react';
import { createContext, useCallback, useContext, useState } from 'react';

import type { UpdateUserRequestBodyDTO } from 'declarations/dto/user';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { StorageE } from 'declarations/enums/storage';
import type { UpdateUserI, UserProfileI } from 'declarations/interfaces/user';
import { useFetchUser, useUpdateUser } from 'hooks/api/useUser';
import { usePendo } from 'hooks/common/usePendo';

export type UserProfileContextT = {
  userProfile: UserProfileI | null;
  isLoadingUserProfile: boolean;
  isSuccessfullyFetchedUserProfile: boolean;
  isUpdatingProfile: boolean;
  isSuccessfullyUpdatedProfile: boolean;
  isColumnPickerPopupOpened: boolean;
  updateUser: (updateUser: UpdateUserI) => void;
  setColumnPickerPopupVisibility: (isOpened: boolean) => void;
};

type UserProfileProviderPropsT = { children: ReactNode };

const contextDefaultValue: UserProfileContextT = {
  userProfile: null,
  isLoadingUserProfile: false,
  isSuccessfullyFetchedUserProfile: false,
  isUpdatingProfile: false,
  isSuccessfullyUpdatedProfile: false,
  updateUser: (_updateUser: UpdateUserI): Promise<null> => {
    return Promise.resolve(null);
  },
  isColumnPickerPopupOpened: false,
  setColumnPickerPopupVisibility: (_isOpened: boolean): undefined => {
    return undefined;
  },
};

export const UserProfileContext = createContext<UserProfileContextT>(contextDefaultValue);

const UserProfileProvider: FC<UserProfileProviderPropsT> = ({ children }): JSX.Element => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();
  const idToken = localStorage.getItem(StorageE.IdToken);
  const [isColumnPickerPopupOpened, setColumnPickerPopupVisibility] = useState<boolean>(false);

  /**
   * Api hooks
   */
  const getUserResult = useFetchUser(idToken);
  const updateUserMutation = useUpdateUser({
    onSuccess: () => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchUser]);
    },
  });

  /**
   * Initialize Pendo when user profile is loaded
   */
  usePendo(getUserResult.data || null, getUserResult.isSuccess);

  /**
   * Handlers
   */
  const updateUser = useCallback(
    (data: UpdateUserI): void => {
      if (
        !data.phoneNumber ||
        !data.firstname ||
        !data.lastname ||
        !getUserResult.data?.userId ||
        !getUserResult.data?.emailAddress
      ) {
        return;
      }
      const file = data.photoFiles[0] ?? null;
      const phoneNumber = data.phoneNumber.replace(/[^a-zA-Z0-9]/g, '');
      const bodyFormData = new FormData();

      bodyFormData.set('UserId', getUserResult.data.userId);
      bodyFormData.set('Firstname', data.firstname);
      bodyFormData.set('Lastname', data.lastname);
      bodyFormData.set('PhoneNumber', phoneNumber);
      bodyFormData.set('Photo', file);
      bodyFormData.set('IsActive', 'true');
      bodyFormData.set('DeletePhoto', 'false');
      bodyFormData.set('Email', getUserResult.data.emailAddress);

      updateUserMutation.mutate(bodyFormData as UpdateUserRequestBodyDTO);
    },
    [getUserResult.data, updateUserMutation]
  );
  return (
    <UserProfileContext.Provider
      value={{
        userProfile: getUserResult.data || null,
        isLoadingUserProfile: getUserResult.isLoading || getUserResult.isRefetching,
        isSuccessfullyFetchedUserProfile: getUserResult.isSuccess,
        isUpdatingProfile: updateUserMutation.isLoading,
        isSuccessfullyUpdatedProfile: updateUserMutation.isSuccess,
        updateUser,
        isColumnPickerPopupOpened,
        setColumnPickerPopupVisibility,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

const useUserProfile = (): UserProfileContextT => useContext(UserProfileContext);

export { UserProfileProvider, useUserProfile };
